import { projectReferenceAdapter } from '../../../react/state/project-reference-state/project-reference-adapter';
import { publishNotification } from '../../../utils/notifications/publish-notification';
import { getGeoidLongitudeRange } from '../../../utils/proj4/geoid';
import { CoordinateManager } from '../../../viewer/coordinate-manager';

export type AvarageError = {
  x: number;
  y: number;
  z: number;
  total: number;
};

type GcpHeaderFormat = {
  xIndex: number;
  yIndex: number;
  zIndex: number;
  nameIndex: number;
};
export class GCP {
  static createFromXml(gcpElement: Element): GCP {
    const reference = gcpElement.children[0];
    if (!gcpElement.hasAttributes() || !reference?.hasAttributes()) {
      throw new Error(`Invalid GCP element or missing reference`);
    }

    const name = gcpElement.getAttribute('label') ?? '';
    const id = Number(gcpElement.getAttribute('id'));

    const easting = Number(reference.getAttribute('x'));
    const northing = Number(reference.getAttribute('y'));
    const height = Number(reference.getAttribute('z'));
    const enabled =
      reference.getAttribute('enabled') === 'true' ||
      Number(reference.getAttribute('enabled')) === 1;

    return new GCP(id, name, easting, northing, height, enabled);
  }

  static parseCsvFile(data: string): GCP[] {
    // split on newline
    const rows: string[] = data.split(/[\n\r]/g);
    let gcpPoints: GCP[] = [];

    gcpPoints = GCP.parseRows(rows, ',');

    // First fallback if comma doesn't parse
    if (gcpPoints.length === 0) {
      gcpPoints = GCP.parseRows(rows, ';');
    }

    // Second fallback if semicolon doesn't parse
    if (gcpPoints.length === 0) {
      gcpPoints = GCP.parseRows(rows, '\t');
    }

    const header = projectReferenceAdapter.getSnapshot().geoid?.header;
    if (header) {
      const proj4 = projectReferenceAdapter.getCoordinateSystemOrDefault().proj4 ?? 'local';

      const checkBoundaries = (showNotification = false) => {
        for (const gcp of gcpPoints) {
          const coordinates = CoordinateManager.getInstance().convertCoords(
            gcp.easting,
            gcp.northing,
            proj4,
            'wgs84',
          );

          const lng = getGeoidLongitudeRange(header, coordinates.x);
          const latOutOfBounds = coordinates.y < header.lat.min || coordinates.y > header.lat.max;
          const lngOutOfBounds = coordinates.x < lng.min || coordinates.x > lng.max;

          if (latOutOfBounds || lngOutOfBounds) {
            if (showNotification) {
              publishNotification('err0903');
            }

            throw Error(
              'GCP-filen innehåller punkter som är utanför begränsningsområdet för projektets geoid.',
            );
          }
        }
      };

      // Check boundaries once, if it is not within boundaries, flip NE and try again.
      try {
        checkBoundaries();
      } catch (err: unknown) {
        for (const gcp of gcpPoints) {
          [gcp.northing, gcp.easting] = [gcp.easting, gcp.northing];
        }

        checkBoundaries(true);
      }
    }

    return gcpPoints;
  }

  private static createFromCsv(columns: string[], headerFormat?: GcpHeaderFormat): GCP {
    const name = columns[headerFormat?.nameIndex ?? 0].replace(/"/g, '');
    const northing = Number.parseFloat(columns[headerFormat?.xIndex ?? 1]);
    const easting = Number.parseFloat(columns[headerFormat?.yIndex ?? 2]);
    const height = Number.parseFloat(columns[headerFormat?.zIndex ?? 3]);

    return new GCP(-1, name, easting, northing, height, true);
  }

  private static parseAsbuiltHeader(values?: string[]): GcpHeaderFormat | undefined {
    values = values?.map((x) => x.replace(/"/g, ''));
    if (
      values &&
      values.includes('x') &&
      values.includes('y') &&
      values.includes('z') &&
      values.includes('localId')
    ) {
      return {
        xIndex: values.indexOf('x'),
        yIndex: values.indexOf('y'),
        zIndex: values.indexOf('z'),
        nameIndex: values.indexOf('localId'),
      };
    }

    return;
  }

  private static parseRows(rows: string[], delimiter: string): GCP[] {
    const gcpPoints: GCP[] = [];

    const headerFormat = this.parseAsbuiltHeader(rows[0]?.split(delimiter));

    for (const row of rows) {
      const columns = row.split(delimiter);
      if (columns.length >= 4) {
        const point = GCP.createFromCsv(columns, headerFormat);

        if (point.isValid()) {
          gcpPoints.push(GCP.createFromCsv(columns, headerFormat));
        }
      }
    }

    return gcpPoints;
  }

  avarageError: AvarageError | undefined;
  get errorX() {
    return this.avarageError?.x.toFixed(3) ?? '';
  }
  get errorY() {
    return this.avarageError?.y.toFixed(3) ?? '';
  }
  get errorZ() {
    return this.avarageError?.z.toFixed(3) ?? '';
  }
  get errorTotal() {
    return this.avarageError?.total.toFixed(3) ?? '';
  }

  constructor(
    public id: number,
    public name: string,
    public easting: number,
    public northing: number,
    public height: number,
    public enabled: boolean,
  ) {}

  isValid(): boolean {
    if (isNaN(this.northing) || isNaN(this.easting) || isNaN(this.height)) {
      return false;
    }

    if (!(this.name && this.name.length)) {
      return false;
    }

    if (!(this.height && this.easting && this.northing)) {
      return false;
    }

    return true;
  }
}
